import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    SafetyCertificateOutlined, SearchOutlined, FileTextOutlined,
    UserAddOutlined, ReloadOutlined, BankOutlined, EnvironmentOutlined, PhoneOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Empty, Input, Row, Spin, Tag, Typography, message } from "antd";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";

const { Text, Title } = Typography;
const PRIMARY = "#242166";

/**
 * Accréditation courtier : liste UNIQUEMENT les courtiers ayant des agréments (conventions de
 * collaboration) SOUMIS mais NON VALIDÉS.
 *  - Compagnie : ses agréments en attente.
 *  - Pool : tous les agréments en attente, toutes compagnies confondues.
 * La création d'un courtier reste séparée (bouton « Nouveau courtier » → /ajouter-courtier).
 */
const AccreditationCourtiers = () => {
    const navigate = useNavigate();
    const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
    const orgCode = LOGGED_USER?.profil?.organisme?.code;

    const [loading, setLoading] = useState(false);
    const [courtiers, setCourtiers] = useState([]);
    const [search, setSearch] = useState("");

    const fetchCourtiers = () => {
        setLoading(true);
        axiosInstance.get(`/api/agence/all/courtiers`)
            .then((r) => setCourtiers(Array.isArray(r.data) ? r.data : []))
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    };
    useEffect(fetchCourtiers, []);

    const agreementKey = (agreement) => agreement?.agreementId || agreement?.compagnie?.code;
    const isValidatedAgreement = (agreement, courtier) => {
        const validAgrements = courtier?.validAgrements || [];
        return validAgrements.some((valid) =>
            valid?.agreementId === agreement?.agreementId
            || (valid?.compagnie?.code && valid.compagnie.code === agreement?.compagnie?.code)
        );
    };

    // Agréments soumis dans le périmètre courant, alignés sur la logique de /courtiers :
    // `validAgrements` = compagnies déjà agréées ; `agrements` = conventions déposées.
    const agreementsOf = (c) => {
        const ag = c?.agrements || [];
        return ag.filter((a) => isPool || a?.compagnie?.code === orgCode);
    };

    const aValider = useMemo(() => {
        const list = courtiers
            .map((c) => {
                const agreements = agreementsOf(c);
                const pending = agreements.filter((a) => !isValidatedAgreement(a, c));
                return { courtier: c, agreements, pending };
            })
            // On reste sur une page d'accréditation : seuls les courtiers ayant au moins
            // une validation à faire sont listés, mais on affiche toutes leurs conventions.
            .filter((x) => x.pending.length > 0);
        const q = search.trim().toLowerCase();
        return q
            ? list.filter((x) => `${x.courtier?.nom || ""} ${x.courtier?.ville || ""} ${x.courtier?.codeAgence || ""}`.toLowerCase().includes(q))
            : list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courtiers, search, isPool, orgCode]);

    const totalAgrements = useMemo(() => aValider.reduce((s, x) => s + x.pending.length, 0), [aValider]);
    const totalConventions = useMemo(() => aValider.reduce((s, x) => s + x.agreements.length, 0), [aValider]);

    const ouvrirDetails = (courtier, agreement) => {
        navigate(`/courtier/${courtier.codeId}`, {
            state: { activeTab: "3", agreementId: agreement?.agreementId },
        });
    };

    const telecharger = (doc) => {
        if (!doc) return;
        const filename = `${doc}`.split("/").pop();
        axiosInstance.get(`/api/agreement/document/${filename}`, { responseType: "blob" })
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url; a.download = filename; a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => message.error("Document indisponible"));
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"MEMBRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {/* En-tête */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#111827" }}>
                                        <SafetyCertificateOutlined style={{ marginRight: 8, color: PRIMARY }} />
                                        Accréditation courtier
                                    </Title>
                                    <Text type="secondary">
                                        {isPool ? "Tous les agréments soumis non encore validés" : "Agréments soumis à votre compagnie, en attente de validation"}
                                    </Text>
                                </div>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <Button icon={<ReloadOutlined />} onClick={fetchCourtiers}>Rafraîchir</Button>
                                    <Button type="primary" icon={<UserAddOutlined />} onClick={() => navigate("/ajouter-courtier")} style={{ background: PRIMARY }}>
                                        Nouveau courtier
                                    </Button>
                                </div>
                            </div>

                            <Spin spinning={loading}>
                                {/* Récap + recherche */}
                                <Row gutter={[12, 12]} style={{ marginBottom: 14 }} align="middle">
                                    <Col xs={12} md={6}>
                                        <Card size="small" bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>Courtiers à valider</div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: PRIMARY }}>{aValider.length}</div>
                                        </Card>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Card size="small" bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>Agréments en attente</div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: "#d97706" }}>{totalAgrements}/{totalConventions}</div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Input allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} placeholder="Rechercher un courtier…" value={search} onChange={(e) => setSearch(e.target.value)} />
                                    </Col>
                                </Row>

                                {aValider.length === 0 ? (
                                    <Empty description="Aucun courtier en attente de validation" style={{ padding: "50px 0" }} />
                                ) : (
                                    <Row gutter={[16, 16]}>
                                        {aValider.map(({ courtier, agreements, pending }) => (
                                            <Col xs={24} lg={12} key={courtier.codeId || courtier.codeAgence}>
                                                <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.07)", height: "100%" }} bodyStyle={{ padding: 18 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                                                        <span style={{ width: 44, height: 44, borderRadius: 10, background: `${PRIMARY}14`, color: PRIMARY, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}><SafetyCertificateOutlined /></span>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{ fontWeight: 700, color: "#111827", fontSize: 16 }}>{courtier.nom}</div>
                                                            <div style={{ fontSize: 12, color: "#9ca3af", display: "flex", gap: 12, flexWrap: "wrap" }}>
                                                                {courtier.ville && <span><EnvironmentOutlined /> {courtier.ville}</span>}
                                                                {courtier.codeAgence && <span>Code {courtier.codeAgence}</span>}
                                                                {(courtier.contact || courtier.telephone1Agence) && <span><PhoneOutlined /> {courtier.telephone1Agence || courtier.contact}</span>}
                                                            </div>
                                                        </div>
                                                        <Tag color="orange" style={{ marginLeft: "auto", borderRadius: 999 }}>{pending.length}/{agreements.length} en attente</Tag>
                                                    </div>

                                                    {/* Conventions déposées */}
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                                        {agreements.map((ag) => {
                                                            const validated = isValidatedAgreement(ag, courtier);
                                                            return (
                                                            <div key={agreementKey(ag)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                                                                <BankOutlined style={{ color: validated ? "#16a34a" : PRIMARY }} />
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div style={{ fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                        {ag?.compagnie?.raisonSociale || "Compagnie"}
                                                                    </div>
                                                                </div>
                                                                <Tag color={validated ? "success" : "warning"} style={{ borderRadius: 999 }}>
                                                                    {validated ? "Validé" : "En attente"}
                                                                </Tag>
                                                                {ag?.document && (
                                                                    <Button size="small" type="text" icon={<FileTextOutlined />} onClick={() => telecharger(ag.document)}>Document</Button>
                                                                )}
                                                                <Button size="small" type="primary" icon={<EyeOutlined />}
                                                                    onClick={() => ouvrirDetails(courtier, ag)}
                                                                    style={{ background: PRIMARY, borderColor: PRIMARY }}>
                                                                    Détails
                                                                </Button>
                                                            </div>
                                                        );})}
                                                    </div>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </Spin>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default AccreditationCourtiers;
